import type { useQuery } from "@apollo/client/react";
import { yupResolver } from "@hookform/resolvers/yup";
import FormInputMenu from "@repo/components/src/forms/menu";
import { Box } from "@repo/components/src/ui/box";
import { Heading } from "@repo/components/src/ui/heading";
import { HStack } from "@repo/components/src/ui/hstack";
import {
  Progress,
  ProgressFilledTrack,
} from "@repo/components/src/ui/progress";
import { SkeletonText } from "@repo/components/src/ui/skeleton";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import {
  getCountryName,
  getGenderName,
} from "@repo/components/src/utils/countries";
import { formatPercentage } from "@repo/components/src/utils/numbers";
import React from "react";
import { useForm, useWatch } from "react-hook-form";
import { AudienceSchema, audienceSchema } from "./schema";
import { AccountMetricTotalValueQuery, AccountMetricTotalValueQueryVariables } from "@repo/graphql/documents/accountMetric/query.generated";

type Props = {
  accountMetricQuery: useQuery.Result<
    AccountMetricTotalValueQuery,
    AccountMetricTotalValueQueryVariables
  >;
};

const AudienceSummary = React.memo((props: Props) => {
  const { accountMetricQuery } = props;
  const { data, loading } = accountMetricQuery;
  const formProps = useForm<AudienceSchema>({
    resolver: yupResolver(audienceSchema),
    defaultValues: {
      topic: "gender",
    },
  });
  const selectedType = useWatch({
    control: formProps.control,
    name: "topic",
  });
  const followerDemographicsMetric = data?.accountMetric.totalValues.find(
    (totalValue) => {
      const accountMetric = totalValue.accountMetric;
      return accountMetric.name === "follower_demographics";
    },
  );
  const audienceMetric = followerDemographicsMetric;
  const breakdownResults =
    audienceMetric?.breakdowns
      ?.find((b) => b.dimensionKeys.includes(selectedType))
      ?.results.slice()
      .sort((a, b) => b.value - a.value)
      .slice(0, 5) ?? [];
  const totalValue = audienceMetric?.value ?? 0;

  return (
    <>
      <HStack space="lg" className="items-center">
        <Heading className="font-medium flex-1 shrink" numberOfLines={1}>
          Audiencia
        </Heading>
        <FormInputMenu
          control={formProps.control}
          name="topic"
          options={[
            { label: "Ciudad", value: "city" },
            { label: "Edad", value: "age" },
            { label: "Género", value: "gender" },
            { label: "País", value: "country" },
          ]}
          size="sm"
          labelSize="sm"
          headingProps={{ bold: false }}
          buttonTriggerProps={{
            rounded: "lg",
            action: "brand",
            size: "xs",
            className: "",
          }}
        />
      </HStack>
      <Box className="mt-4">
        <SkeletonText
          className="h-4 rounded-lg"
          isLoaded={!loading}
          _lines={5}
          gap={4}
        >
          <VStack space="md">
            {breakdownResults.map((result) => {
              return (
                <Box key={result.dimensionValues[0]}>
                  <HStack space="lg">
                    <Text size="md" className="flex-1" numberOfLines={1}>
                      {selectedType === "country"
                        ? getCountryName(result.dimensionValues[0])
                        : selectedType === "gender"
                          ? getGenderName(result.dimensionValues[0])
                          : result.dimensionValues[0]}
                    </Text>
                    <Text className="text-right">
                      {formatPercentage(result.value / (totalValue || 1), 0)}
                    </Text>
                  </HStack>
                  <Progress
                    action="brand"
                    size="sm"
                    value={result.value}
                    max={totalValue}
                  >
                    <ProgressFilledTrack />
                  </Progress>
                </Box>
              );
            })}
          </VStack>
        </SkeletonText>
      </Box>
    </>
  );
});

AudienceSummary.displayName = "AudienceSummary";

export default AudienceSummary;
